import * as React from 'react';

import { IOwner, IEditablePet, ISelectOption } from '../../types';

import { url, safeJsonParse, createFetchOptions } from '../../util';

import LoadingPanel from './LoadingPanel';
import PetEditor from './PetEditor';

import createPetEditorModel from './createPetEditorModel';

interface IEditPetPageProps {
  params: {
    ownerId: string,
    petId: string
  };
}

interface IEditPetPageState {
  pet?: IEditablePet;
  owner?: IOwner;
  pettypes?: ISelectOption[];
};

export default class EditPetPage extends React.Component<IEditPetPageProps, IEditPetPageState> {
  private _isMounted: boolean = false;

  componentDidMount() {
    this._isMounted = true;
    const { params } = this.props;

    // Use the simpler /api/pets/{petId} endpoint instead of /api/owners/{ownerId}/pets/{petId}
    // This avoids the 400 error when pet doesn't match owner (the pet DTO includes ownerId anyway)
    const fetchUrl = url(`/api/pets/${params.petId}`);
    console.log('EditPetPage: Fetching pet from:', fetchUrl);

    const loadPetPromise = fetch(fetchUrl, createFetchOptions())
      .then(response => {
        console.log('EditPetPage: Response status:', response.status, 'URL:', fetchUrl);
        // Check if response is an error
        if (response.status >= 400) {
          console.error('EditPetPage: API returned error status:', response.status);
          return safeJsonParse(response).then(data => {
            console.error('EditPetPage: Error response data:', data);
            // If it's an error response, throw it
            const errorMessage = data.message || data.error || `HTTP ${response.status}: ${response.statusText}`;
            throw new Error(errorMessage);
          });
        }
        // Parse successful response
        return safeJsonParse(response).then(data => {
          console.log('EditPetPage: Parsed response data:', data);
          // Validate that we got actual pet data, not an error object
          if (!data) {
            console.error('EditPetPage: No data received');
            throw new Error('No data received from server');
          }
          // Check if it's an error object (has message property but no id/name)
          if (data.message && !data.id && !data.name) {
            console.error('EditPetPage: Received error object instead of pet data:', data);
            throw new Error(data.message || 'Error loading pet data');
          }
          // Validate that we have at least an id or name (basic pet data)
          if (!data.id && !data.name) {
            console.error('EditPetPage: Invalid pet data - missing id and name:', data);
            throw new Error('Invalid pet data received from server');
          }
          console.log('EditPetPage: Valid pet data received:', data);
          return data;
        });
      })
      .catch(error => {
        console.error('EditPetPage: Error loading pet:', error);
        throw error;
      });

    createPetEditorModel(this.props.params.ownerId, loadPetPromise)
      .then(model => {
        if (this._isMounted) {
          // CRITICAL: Validate pet data before setting state
          if (!model.pet) {
            console.error('EditPetPage: No pet data in model:', model);
            this.setState({ pet: undefined, owner: undefined, pettypes: undefined });
            return;
          }

          // Check if pet is actually an error object
          if (model.pet.message || (!model.pet.id && !model.pet.name)) {
            console.error('EditPetPage: Pet data is invalid or error object:', model.pet);
            this.setState({ pet: undefined, owner: undefined, pettypes: undefined });
            return;
          }

          // CRITICAL: Ensure pet has isNew set to false for edit mode
          // Also ensure pet data is in the correct format for PetEditor
          // The pet from backend has type object, but PetEditor expects it in backend format
          // PetEditor will convert it using convertPetToEditable
          // Just ensure isNew is false
          model.pet.isNew = false;
          // Ensure pet has an ID (it should from the backend)
          if (model.pet.id === null || model.pet.id === undefined) {
            console.warn('EditPetPage: Pet loaded without ID, this should not happen');
          }
          console.log('EditPetPage: Pet data from backend:', model.pet);
          console.log('EditPetPage: Setting state with model:', model);
          this.setState(model);
        }
      })
      .catch(error => {
        console.error('EditPetPage: Error loading pet model:', error);
        if (this._isMounted) {
          this.setState({ pet: undefined, owner: undefined, pettypes: undefined });
        }
      });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  render() {
    // Check if state exists and has required properties
    if (!this.state) {
      return <LoadingPanel />;
    }
    
    const { pet, owner, pettypes } = this.state;
    
    // Don't render until all required data is loaded
    if (!pet || !owner || !pettypes) {
      return <LoadingPanel />;
    }

    return <PetEditor pet={pet} owner={owner} pettypes={pettypes} />;
  }
}
